import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuthStore, type Role } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { getMissingRoleRedirectPath } from '@/features/lawyer-cert/navigation'

/** 未鉴权时跳到 /u/ai-doc 并唤起登录弹窗 */
function RedirectWithModal() {
  const location = useLocation()
  const openLoginModal = useUiStore((s) => s.openLoginModal)
  useEffect(() => {
    openLoginModal()
  }, [openLoginModal])
  const redirectTo = `${location.pathname}${location.search}${location.hash}`
  return <Navigate to={`/u/ai-doc?redirectTo=${encodeURIComponent(redirectTo)}`} replace />
}

/**
 * 已登录守卫
 * 未登录 → 跳 /u/ai-doc 并弹登录框
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore(
    (s) => s.accessToken !== null && (!s.expiresAt || Date.now() < s.expiresAt),
  )
  if (!isAuthenticated) return <RedirectWithModal />
  return <>{children}</>
}

/**
 * 角色守卫
 * - 未登录 → 跳 /u/ai-doc + 弹登录框
 * - 无对应角色 → 跳 /u/ai-doc
 */
export function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const isAuthenticated = useAuthStore(
    (s) => s.accessToken !== null && (!s.expiresAt || Date.now() < s.expiresAt),
  )
  const roles = useAuthStore((s) => s.roles)

  if (!isAuthenticated) return <RedirectWithModal />
  if (!roles.includes(role)) return <Navigate to={getMissingRoleRedirectPath(role)} replace />
  return <>{children}</>
}
